import type { PointsPackConfig } from './types'

/**
 * 积分加量包配置走 KV（后端配置中心）。
 *
 * 读取链路：fetchConfig（POST /web/config/query，传 key）→ 写入 Redux config →
 * 组件按 POINTS_PACK_CONFIG_KEY 取出 value → parsePointsPackConfig 解析。
 * 解析失败 / 未配置时回退到 DEFAULT_POINTS_PACK_CONFIG。
 *
 * 后端在配置中心按 key=POINTS_PACK_CONFIG_KEY 配置，value 填下面这段 JSON
 * （字符串或对象都行，解析两种都兼容）：
 *
 *   {
 *     "title": "加量包",
 *     "titleSuffix": "特惠开放中",
 *     "packages": [
 *       { "key": "standard",   "name": "标准包",   "goodsId": "<商品id>", "points": "1000万", "price": 199,
 *         "reports": "≈ 50 份报表",  "queries": "或 200 次问数",  "hours": "节省 100+ 工时" },
 *       { "key": "value",      "name": "划算包",   "goodsId": "<商品id>", "points": "3000万", "price": 499,
 *         "reports": "≈ 150 份报表", "queries": "或 600 次问数",  "hours": "节省 300+ 工时", "tag": "最划算", "recommended": true },
 *       { "key": "large",      "name": "大容量包", "goodsId": "<商品id>", "points": "5000万", "price": 799,
 *         "reports": "≈ 250 份报表", "queries": "或 1000 次问数", "hours": "节省 500+ 工时" },
 *       { "key": "enterprise", "name": "企业包",   "goodsId": "<商品id>", "points": "1亿",    "price": 1399,
 *         "reports": "≈ 500 份报表", "queries": "或 2000 次问数", "hours": "节省 1000+ 工时" }
 *     ]
 *   }
 *
 * 关键约定：只有 goodsId 是下单必需（前端拿它走 /web/order/create）；其余都是
 * 展示文案，可在 KV 里随时改。
 */

/** KV 配置 key —— 需与后端配置中心约定一致 */
export const POINTS_PACK_CONFIG_KEY = 'web_ai_points_pack'

/** 解析失败 / 未配置时的兜底（goodsId 留空，必须由后端 KV 配真实 id 才能下单） */
export const DEFAULT_POINTS_PACK_CONFIG: PointsPackConfig = {
  title: '加量包',
  titleSuffix: '特惠开放中',
  packages: [
    {
      key: 'standard',
      name: '标准包',
      goodsId: '',
      points: '1000万',
      price: 199,
      reports: '≈ 50 份报表',
      queries: '或 200 次问数',
      hours: '节省 100+ 工时'
    },
    {
      key: 'value',
      name: '划算包',
      goodsId: '',
      points: '3000万',
      price: 499,
      reports: '≈ 150 份报表',
      queries: '或 600 次问数',
      hours: '节省 300+ 工时',
      tag: '最划算',
      recommended: true
    },
    {
      key: 'large',
      name: '大容量包',
      goodsId: '',
      points: '5000万',
      price: 799,
      reports: '≈ 250 份报表',
      queries: '或 1000 次问数',
      hours: '节省 500+ 工时'
    },
    {
      key: 'enterprise',
      name: '企业包',
      goodsId: '',
      points: '1亿',
      price: 1399,
      reports: '≈ 500 份报表',
      queries: '或 2000 次问数',
      hours: '节省 1000+ 工时'
    }
  ]
}

/** 把 KV value（JSON 字符串或对象）解析成 PointsPackConfig，任何异常都回退默认 */
export function parsePointsPackConfig(raw: unknown): PointsPackConfig {
  if (!raw) return DEFAULT_POINTS_PACK_CONFIG
  try {
    const obj = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!obj || !Array.isArray(obj.packages) || obj.packages.length === 0) {
      return DEFAULT_POINTS_PACK_CONFIG
    }
    return {
      title: obj.title || DEFAULT_POINTS_PACK_CONFIG.title,
      titleSuffix: obj.titleSuffix || DEFAULT_POINTS_PACK_CONFIG.titleSuffix,
      packages: obj.packages
    }
  } catch {
    return DEFAULT_POINTS_PACK_CONFIG
  }
}
